import { atom } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';

export type StoredMeasurement = {
  id: string;
  name: string;
  type: 'distance' | 'area';
  points: [number, number, number][];
  createdAt: number;
  enabled: boolean;
  showComponents?: boolean;
};

const measurementsStorageKey = (projectId: string) => `@actegon.gis.measurements.${projectId}`;

export const measurementsAtom = atomFamily((projectId: string) =>
  atomWithStorage<StoredMeasurement[]>(
    measurementsStorageKey(projectId),
    [],
    createJSONStorage<StoredMeasurement[]>(() => localStorage),
  ),
);

export const addMeasurementAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, measurement: Omit<StoredMeasurement, 'id' | 'name' | 'createdAt' | 'enabled'>) => {
    const current = get(measurementsAtom(projectId));
    const id = crypto.randomUUID();
    const newMeasurement: StoredMeasurement = {
      ...measurement,
      id,
      name: '',
      createdAt: Date.now(),
      enabled: true,
    };
    set(measurementsAtom(projectId), [...current, newMeasurement]);
    return id;
  }),
);

export const updateMeasurementPointsAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, { id, points }: { id: string; points: [number, number, number][] }) => {
    const current = get(measurementsAtom(projectId));
    set(
      measurementsAtom(projectId),
      current.map((m) => (m.id === id ? { ...m, points } : m)),
    );
  }),
);

export const toggleMeasurementAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, measurementId: string) => {
    const current = get(measurementsAtom(projectId));
    set(
      measurementsAtom(projectId),
      current.map((m) => (m.id === measurementId ? { ...m, enabled: !m.enabled } : m)),
    );
  }),
);

export const renameMeasurementAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, { id, name }: { id: string; name: string }) => {
    const current = get(measurementsAtom(projectId));
    set(
      measurementsAtom(projectId),
      current.map((m) => (m.id === id ? { ...m, name } : m)),
    );
  }),
);

export const toggleComponentsAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, measurementId: string) => {
    const current = get(measurementsAtom(projectId));
    set(
      measurementsAtom(projectId),
      current.map((m) => (m.id === measurementId ? { ...m, showComponents: !m.showComponents } : m)),
    );
  }),
);

export const deleteMeasurementAtom = atomFamily((projectId: string) =>
  atom(null, (get, set, measurementId: string) => {
    const current = get(measurementsAtom(projectId));
    set(
      measurementsAtom(projectId),
      current.filter((m) => m.id !== measurementId),
    );
  }),
);

/** ID of the measurement currently being edited by a measuring tool (skip rendering in SavedMeasurements). */
export const activeMeasurementIdAtom = atom<string | null>(null);
